import React, { createContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { loadTranslations, saveTranslations } from "./translations";
import {
  flattenTranslations,
  unflattenTranslations,
} from "../utils/translationUtils";
import { routesMap } from "../routesMap";

export const TranslationContext = createContext();

export const TranslationProvider = ({ children }) => {
  const { i18n, ready } = useTranslation("global");
  const location = useLocation();

  // Translation states
  const [translations, setTranslations] = useState({});
  const [otherTranslations, setOtherTranslations] = useState({});
  const [flatTranslations, setFlatTranslations] = useState({});
  const [otherFlatTranslations, setOtherFlatTranslations] = useState({});
  const [initialFlatTranslations, setInitialFlatTranslations] = useState({});
  const [initialOtherFlatTranslations, setInitialOtherFlatTranslations] =
    useState({});

  // UI states
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [sections, setSections] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  // Menu-specific states
  const [initialMenuTranslations, setInitialMenuTranslations] = useState(null);
  const [initialOtherMenuTranslations, setInitialOtherMenuTranslations] =
    useState(null);
  const [collapsedItems, setCollapsedItems] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [toggleOtherKeys, setToggleOtherKeys] = useState({});
  const [menuItemToggleOther, setMenuItemToggleOther] = useState({});
  const [sectionToggleOther, setSectionToggleOther] = useState({});
  const [allergens, setAllergens] = useState({});

  // Confirmation modal states
  const [confirmMissing, setConfirmMissing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState(null);

  // New section/item modal states
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [selectedSection, setSelectedSection] = useState("");
  const [newItem, setNewItem] = useState({
    key: "",
    name: "",
    desc: "",
    price: "",
    allergens: [],
  });
  const [newSection, setNewSection] = useState({
    key: "",
    title: "",
  });

  // Image manager states
  const [showImageManager, setShowImageManager] = useState(false);
  const [currentDirectory, setCurrentDirectory] = useState("");
  const [imageList, setImageList] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Special states for product editor
  const [showProductEditor, setShowProductEditor] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Helper: Get current page key from the URL
  const getCurrentPage = useCallback(() => {
    const path = location.pathname.toLowerCase();
    const pathToKey = {
      "/menu-carta": "menuCarta",
      "/wine-list": "wineList",
      "/aperitivo-menu": "menuAperitivo",
      "/gift-card": "giftCard",
      "/about": "aboutUs",
      "/celebrations": "celebrations",
      "/reviews": "reviews",
      "/privacy": "privacy",
      "/": "home",
      "/contacts": "contact",
    };

    for (const [basePath, localizedPaths] of Object.entries(routesMap)) {
      if (localizedPaths.it === path || localizedPaths.en === path) {
        return pathToKey[basePath] || basePath.replace("/", "");
      }
    }
    return "home";
  }, [location.pathname]);

  // Helper: Returns the secondary language
  const getSecondaryLanguage = useCallback(() => {
    return i18n.language === "en" ? "it" : "en";
  }, [i18n.language]);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      setShowSidebar(window.innerWidth >= 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Load translations for both languages
  useEffect(() => {
    if (!ready) return;

    const fetchTranslations = async () => {
      setLoading(true);
      const currentLang = i18n.language;
      const secondaryLang = getSecondaryLanguage();

      try {
        // Get current language translations
        let currentTranslations =
          i18n.getResourceBundle(currentLang, "global") || {};
        // Try to load the most recent translations from server
        try {
          const freshCurrentData = await loadTranslations(currentLang);
          currentTranslations = freshCurrentData;
          i18n.addResourceBundle(
            currentLang,
            "global",
            freshCurrentData,
            true,
            true
          );
        } catch (e) {
          console.warn(
            "Could not fetch fresh translations, using local version",
            e
          );
        }

        // Get secondary language translations
        let secondaryTranslations =
          i18n.getResourceBundle(secondaryLang, "global") || {};
        try {
          const freshSecondaryData = await loadTranslations(secondaryLang);
          secondaryTranslations = freshSecondaryData;
          i18n.addResourceBundle(
            secondaryLang,
            "global",
            freshSecondaryData,
            true,
            true
          );
        } catch (e) {
          console.warn("Could not fetch secondary language translations", e);
        }

        // Set up translation states
        setTranslations(currentTranslations);
        setOtherTranslations(secondaryTranslations);

        const flatCurr = flattenTranslations(currentTranslations);
        const flatOther = flattenTranslations(secondaryTranslations);

        setFlatTranslations(flatCurr);
        setOtherFlatTranslations(flatOther);
        setInitialFlatTranslations(flatCurr);
        setInitialOtherFlatTranslations(flatOther);

        // Get allergens for menu pages
        if (currentTranslations?.menuCarta?.allergens?.items) {
          setAllergens(currentTranslations.menuCarta.allergens.items);
        }

        // For menu pages, save initial state of nested sections
        const currentPage = getCurrentPage();
        if (currentTranslations[currentPage]?.sections) {
          const sections = currentTranslations[currentPage].sections;

          // Initialize collapsed states for sections and items
          const initCollapsedSections = {};
          const initCollapsedItems = {};

          Object.keys(sections).forEach((sectionKey) => {
            initCollapsedSections[sectionKey] = true;
            const items = sections[sectionKey].items || {};
            Object.keys(items).forEach((itemKey) => {
              initCollapsedItems[`${sectionKey}-${itemKey}`] = true;
            });
          });

          setCollapsedSections(initCollapsedSections);
          setCollapsedItems(initCollapsedItems);
          setInitialMenuTranslations(JSON.parse(JSON.stringify(sections)));
        }

        if (secondaryTranslations[currentPage]?.sections) {
          setInitialOtherMenuTranslations(
            JSON.parse(
              JSON.stringify(secondaryTranslations[currentPage].sections)
            )
          );
        }

        // Organize translations into sections for navigation
        organizeSections(flatCurr, currentPage);
      } catch (error) {
        console.error("Error loading translations:", error);
        setMessage({ type: "error", text: "Failed to load translations" });
      } finally {
        setLoading(false);
      }
    };

    fetchTranslations();
  }, [i18n, ready, getCurrentPage, getSecondaryLanguage]);

  // Organize translations into sections for navigation
  const organizeSections = (flatTranslations, currentPage) => {
    if (Object.keys(flatTranslations).length === 0) return;

    // First identify all top-level sections
    const sectionMap = {};

    Object.keys(flatTranslations).forEach((key) => {
      const section = key.split(".")[0];
      if (!sectionMap[section]) {
        sectionMap[section] = [];
      }
      sectionMap[section].push(key);
    });

    // Create an array of sections
    const sectionArray = Object.entries(sectionMap).map(([name, keys]) => ({
      name,
      keys,
    }));

    setSections(sectionArray);

    // Set active section based on current page or default to first section
    const pageSectionName = currentPage === "aboutUs" ? "about" : currentPage;
    const matchingSection = sectionArray.find(
      (section) => section.name === pageSectionName
    );

    if (matchingSection) {
      setActiveSection(matchingSection.name);
    } else if (sectionArray.length > 0) {
      setActiveSection(sectionArray[0].name);
    }
  };

  // Handle section change
  const handleSectionChange = useCallback(
    (sectionName) => {
      setActiveSection(sectionName);
      if (isMobile) setShowSidebar(false);

      // Force reload all data for this section
      const currentLang = i18n.language;
      const secondaryLang = getSecondaryLanguage();

      try {
        // Get current language translations
        let currentTranslations =
          i18n.getResourceBundle(currentLang, "global") || {};

        // Get secondary language translations
        let secondaryTranslations =
          i18n.getResourceBundle(secondaryLang, "global") || {};

        // Update translations state
        setTranslations({ ...currentTranslations });
        setOtherTranslations({ ...secondaryTranslations });

        // Update flat translations
        const flatCurr = flattenTranslations(currentTranslations);
        const flatOther = flattenTranslations(secondaryTranslations);
        setFlatTranslations(flatCurr);
        setOtherFlatTranslations(flatOther);
        setInitialFlatTranslations(flatCurr);
        setInitialOtherFlatTranslations(flatOther);

        // Check if new section is a menu page and update menu-specific state
        const isMenuPage = ["menuCarta", "wineList", "menuAperitivo"].includes(
          sectionName
        );
        if (isMenuPage && currentTranslations[sectionName]?.sections) {
          const sections = currentTranslations[sectionName].sections;

          // Initialize section and item collapse states
          const initCollapsedSections = {};
          const initCollapsedItems = {};

          Object.keys(sections).forEach((sectionKey) => {
            initCollapsedSections[sectionKey] = true;
            const items = sections[sectionKey].items || {};
            Object.keys(items).forEach((itemKey) => {
              initCollapsedItems[`${sectionKey}-${itemKey}`] = true;
            });
          });

          setCollapsedSections(initCollapsedSections);
          setCollapsedItems(initCollapsedItems);
          setInitialMenuTranslations(JSON.parse(JSON.stringify(sections)));

          if (secondaryTranslations[sectionName]?.sections) {
            setInitialOtherMenuTranslations(
              JSON.parse(
                JSON.stringify(secondaryTranslations[sectionName].sections)
              )
            );
          }
        }

        // Get allergens for menu pages
        if (currentTranslations?.menuCarta?.allergens?.items) {
          setAllergens(currentTranslations.menuCarta.allergens.items);
        }
      } catch (error) {
        console.error("Error loading section data:", error);
        setMessage({ type: "error", text: "Failed to load section data" });
      }
    },
    [i18n, getSecondaryLanguage, isMobile]
  );

  // SAVE FUNCTION
  const handleSave = async () => {
    setIsSaving(true);
    setMessage({ type: "loading", text: "Checking translations..." });

    const currentPage = getCurrentPage();
    const isMenuPage = ["menuCarta", "wineList", "menuAperitivo"].includes(
      currentPage
    );

    if (isMenuPage) {
      // Check for menu item fields that were edited in current language
      // but the corresponding other language field was not updated.
      let editedMenuFields = [];
      if (initialMenuTranslations && initialOtherMenuTranslations) {
        const currentMenu = translations[currentPage].sections;
        const initialMenu = initialMenuTranslations;
        const currentOtherMenu = otherTranslations[currentPage]?.sections || {};
        const initialOtherMenu = initialOtherMenuTranslations || {};

        for (const section in currentMenu) {
          for (const itemKey in currentMenu[section].items) {
            const currentItem = currentMenu[section].items[itemKey];
            const initialItem = initialMenu[section]?.items[itemKey] || {};
            const currentOtherItem =
              currentOtherMenu[section]?.items[itemKey] || {};
            const initialOtherItem =
              initialOtherMenu[section]?.items[itemKey] || {};

            for (const field in currentItem) {
              if (
                JSON.stringify(currentItem[field]) !==
                  JSON.stringify(initialItem[field]) &&
                (currentOtherItem[field] === undefined ||
                  JSON.stringify(currentOtherItem[field]) ===
                    JSON.stringify(initialOtherItem[field]))
              ) {
                editedMenuFields.push(`${section}.${itemKey}.${field}`);
              }
            }
          }
        }

        // Also check page text strings for menu pages
        const editedKeys = Object.keys(flatTranslations).filter(
          (key) =>
            key.startsWith(`${currentPage}.`) &&
            !key.includes(".sections.") &&
            flatTranslations[key] !== initialFlatTranslations[key]
        );

        const missingOtherKeys = editedKeys.filter(
          (key) =>
            !otherFlatTranslations[key] ||
            otherFlatTranslations[key] === "" ||
            otherFlatTranslations[key] === initialOtherFlatTranslations[key]
        );

        if (missingOtherKeys.length > 0) {
          editedMenuFields = [...editedMenuFields, ...missingOtherKeys];
        }
      }

      if (editedMenuFields.length > 0 && !confirmMissing) {
        setConfirmMissing({ edited: editedMenuFields });
        setIsSaving(false);
        return;
      }
    } else {
      // Regular pages check
      const editedKeys = Object.keys(flatTranslations).filter(
        (key) => flatTranslations[key] !== initialFlatTranslations[key]
      );

      const missingOtherKeys = editedKeys.filter(
        (key) =>
          !otherFlatTranslations[key] ||
          otherFlatTranslations[key] === "" ||
          otherFlatTranslations[key] === initialOtherFlatTranslations[key]
      );

      if (missingOtherKeys.length > 0 && !confirmMissing) {
        setConfirmMissing({ edited: editedKeys, missing: missingOtherKeys });
        setIsSaving(false);
        return;
      }
    }

    setMessage({ type: "loading", text: "Saving translations..." });

    try {
      let dataToSaveCurrent, dataToSaveOther;

      if (isMenuPage) {
        // Create deep copies to avoid modifying original state
        dataToSaveCurrent = JSON.parse(JSON.stringify(translations));
        dataToSaveOther = JSON.parse(JSON.stringify(otherTranslations));

        // For regular strings on menu pages, manually merge from flatTranslations
        Object.keys(flatTranslations).forEach((key) => {
          if (
            key.startsWith(`${currentPage}.`) &&
            !key.includes(".sections.")
          ) {
            // Use a helper function to set nested property
            setNestedProperty(dataToSaveCurrent, key, flatTranslations[key]);
            setNestedProperty(dataToSaveOther, key, otherFlatTranslations[key]);
          }
        });
      } else {
        // For regular pages, just use the flattened translations
        dataToSaveCurrent = unflattenTranslations(flatTranslations);
        dataToSaveOther = unflattenTranslations(otherFlatTranslations);
      }

      const resultCurrent = await saveTranslations(
        i18n.language,
        dataToSaveCurrent
      );

      const secondaryLang = getSecondaryLanguage();
      const resultOther = await saveTranslations(
        secondaryLang,
        dataToSaveOther
      );

      // Update i18n resources
      i18n.addResourceBundle(
        i18n.language,
        "global",
        dataToSaveCurrent,
        true,
        true
      );
      i18n.addResourceBundle(
        secondaryLang,
        "global",
        dataToSaveOther,
        true,
        true
      );

      setMessage({
        type: "success",
        text: `Translations saved successfully for both languages!`,
      });

      // Close editor and refresh page
      setTimeout(() => {
        closeEditor();
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Save error:", error);
      setMessage({
        type: "error",
        text: error.message || "Error saving translations",
      });
    } finally {
      setIsSaving(false);
      setConfirmMissing(null);
    }
  };

  // Helper function to set a nested property using a dot-notation string
  const setNestedProperty = (obj, path, value) => {
    const parts = path.split(".");
    let current = obj;

    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }

    current[parts[parts.length - 1]] = value;
  };

  const closeEditor = () => {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
    setIsEditMode(false);
  };

  // Image Manager Functions
  const fetchImages = async (directory = "") => {
    setIsImageLoading(true);
    try {
      const response = await fetch(
        `/api.php?action=getImages&directory=${directory}`
      );
      if (!response.ok) throw new Error("Failed to fetch images");
      const data = await response.json();
      if (data.success) {
        setImageList(data.items);
        setCurrentDirectory(data.directory);
      } else {
        throw new Error(data.error || "Failed to fetch images");
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to fetch images",
      });
    } finally {
      setIsImageLoading(false);
    }
  };

  const uploadImage = async (file, directory = "") => {
    setIsImageLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(
        `/api.php?action=uploadImage&directory=${directory}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Failed to upload image");
      const data = await response.json();
      if (data.success) {
        fetchImages(directory);
        setMessage({
          type: "success",
          text: "Image uploaded successfully",
        });
        return data.file;
      } else {
        throw new Error(data.error || "Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to upload image",
      });
      return null;
    } finally {
      setIsImageLoading(false);
    }
  };

  const deleteImage = async (path) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(`/api.php?action=deleteImage`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) throw new Error("Failed to delete image");
      const data = await response.json();
      if (data.success) {
        fetchImages(currentDirectory);
        setMessage({
          type: "success",
          text: "Image deleted successfully",
        });
        return true;
      } else {
        throw new Error(data.error || "Failed to delete image");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to delete image",
      });
      return false;
    } finally {
      setIsImageLoading(false);
    }
  };

  const renameImage = async (path, newName) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(`/api.php?action=renameImage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path, newName }),
      });

      if (!response.ok) throw new Error("Failed to rename image");
      const data = await response.json();
      if (data.success) {
        fetchImages(currentDirectory);
        setMessage({
          type: "success",
          text: "Image renamed successfully",
        });
        return data.file;
      } else {
        throw new Error(data.error || "Failed to rename image");
      }
    } catch (error) {
      console.error("Error renaming image:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to rename image",
      });
      return null;
    } finally {
      setIsImageLoading(false);
    }
  };

  const createDirectory = async (path) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(`/api.php?action=createDirectory`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) throw new Error("Failed to create directory");
      const data = await response.json();
      if (data.success) {
        fetchImages(currentDirectory);
        setMessage({
          type: "success",
          text: "Directory created successfully",
        });
        return data.directory;
      } else {
        throw new Error(data.error || "Failed to create directory");
      }
    } catch (error) {
      console.error("Error creating directory:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to create directory",
      });
      return null;
    } finally {
      setIsImageLoading(false);
    }
  };

  const deleteDirectory = async (path) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(`/api.php?action=deleteDirectory`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path }),
      });

      if (!response.ok) throw new Error("Failed to delete directory");
      const data = await response.json();
      if (data.success) {
        // Go to parent directory
        const parentDir = path.includes("/")
          ? path.substring(0, path.lastIndexOf("/"))
          : "";
        fetchImages(parentDir);

        setMessage({
          type: "success",
          text: "Directory deleted successfully",
        });
        return true;
      } else {
        throw new Error(data.error || "Failed to delete directory");
      }
    } catch (error) {
      console.error("Error deleting directory:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to delete directory",
      });
      return false;
    } finally {
      setIsImageLoading(false);
    }
  };

  // Initialize the context value
  const contextValue = {
    // Translation states
    translations,
    setTranslations,
    otherTranslations,
    setOtherTranslations,
    flatTranslations,
    setFlatTranslations,
    otherFlatTranslations,
    setOtherFlatTranslations,
    initialFlatTranslations,
    initialOtherFlatTranslations,
    initialMenuTranslations,
    initialOtherMenuTranslations,

    // UI states
    isEditMode,
    setIsEditMode,
    isSaving,
    message,
    setMessage,
    loading,
    activeSection,
    setActiveSection,
    sections,
    isMobile,
    showSidebar,
    setShowSidebar,

    // Menu-specific states
    collapsedItems,
    setCollapsedItems,
    collapsedSections,
    setCollapsedSections,
    toggleOtherKeys,
    setToggleOtherKeys,
    menuItemToggleOther,
    setMenuItemToggleOther,
    sectionToggleOther,
    setSectionToggleOther,
    allergens,

    // Confirmation modal states
    confirmMissing,
    setConfirmMissing,
    confirmDelete,
    setConfirmDelete,
    confirmDeleteSection,
    setConfirmDeleteSection,

    // New section/item modal states
    showAddItem,
    setShowAddItem,
    showAddSection,
    setShowAddSection,
    selectedSection,
    setSelectedSection,
    newItem,
    setNewItem,
    newSection,
    setNewSection,

    // Image manager states
    showImageManager,
    setShowImageManager,
    currentDirectory,
    setCurrentDirectory,
    imageList,
    selectedImage,
    setSelectedImage,
    isImageLoading,

    // Product editor states
    showProductEditor,
    setShowProductEditor,
    selectedProduct,
    setSelectedProduct,

    // Helper methods
    getCurrentPage,
    getSecondaryLanguage,
    handleSectionChange,
    setNestedProperty,

    // CRUD operations
    handleSave,
    closeEditor,

    // Image manager functions
    fetchImages,
    uploadImage,
    deleteImage,
    renameImage,
    createDirectory,
    deleteDirectory,
  };

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;
